import { useCallback, useEffect, useRef, useState } from 'react';
import type { SerializeFrom } from '@remix-run/node';
import { useFetcher } from '@remix-run/react';
import {
    CONVERSATIONS_API,
    type loader,
} from '~/routes/remix-api.chat.conversations';
import { useConversationCache } from './use-conversation-cache';
import type { PartialConversation } from '../types';

type ConversationsResult = NonNullable<SerializeFrom<typeof loader>>;
type PageInfo = ConversationsResult['pageInfo'];

/**
 * Hook to get the user's conversations.
 */
export function useConversations() {
    const { lastCachedConversation } = useConversationCache();
    const isFetched = useRef(false);
    const [conversations, setConversations] = useState<
        PartialConversation[] | null
    >(null);
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
    const fetcher = useFetcher<typeof loader>();
    // TODO: pagination, sorting, filtering

    useEffect(() => {
        // Fetch initial conversations on mount.
        if (fetcher.state === 'idle' && !isFetched.current) {
            fetcher.load(CONVERSATIONS_API);
            isFetched.current = true;
        } else if (
            isFetched.current &&
            !fetcher.data &&
            fetcher.state === 'idle'
        ) {
            // If the conversations are still not fetched, try again.
            fetcher.load(CONVERSATIONS_API);
        } else if (fetcher.data) {
            setConversations(fetcher.data.conversations);
            setPageInfo(fetcher.data.pageInfo);
        }
    }, [fetcher]);

    // Update the chat settings and unread message count as soon as possible
    // from the conversation cache.
    useEffect(() => {
        if (!lastCachedConversation) return;
        setConversations((prev) => {
            if (prev?.some((c) => c.ID === String(lastCachedConversation.ID))) {
                return prev.map((c) =>
                    c.ID === String(lastCachedConversation.ID)
                        ? {
                              ...c,
                              UnreadMessageCount:
                                  lastCachedConversation.UnreadMessageCount,
                              Unread: lastCachedConversation.Unread,
                              ...(lastCachedConversation.__typename ===
                              'DirectConversation'
                                  ? { Name: lastCachedConversation.Name }
                                  : {}),
                          }
                        : c
                );
            }
            return prev;
        });
    }, [lastCachedConversation]);

    return {
        conversations,
        pageInfo,
        refetch: useCallback(() => {
            fetcher.load(CONVERSATIONS_API);
        }, [fetcher]),
    };
}
